import type { CartItem, Customization, Ingredient, Product, ProductId, SelectedExtra } from "../types";

export const CART_STORAGE_KEY = "@NinjaBurger:cart";

const round2 = (value: number) => Math.round(value * 100) / 100;

/** Remove adicionais zerados e espaços; personalização vazia vira null. */
const normalizeCustomization = (c: Customization | null | undefined): Customization | null => {
  if (!c) return null;
  const normalized: Customization = {
    removed: c.removed,
    extras: c.extras.filter((extra) => extra.quantity > 0),
    notes: c.notes.trim(),
  };
  const hasContent = normalized.removed.length > 0 || normalized.extras.length > 0 || normalized.notes !== "";
  return hasContent ? normalized : null;
};

/**
 * Chave da linha do carrinho. Lanche sem personalização usa só o id (como na
 * versão anterior); com personalização, entram removidos, adicionais e observação.
 */
export const lineIdFor = (productId: ProductId, customization: Customization | null): string => {
  if (!customization) return String(productId);
  const removed = customization.removed.map((i) => i.id).sort((a, b) => a - b).join(",");
  const extras = customization.extras
    .map((e) => `${e.id}x${e.quantity}`)
    .sort()
    .join(",");
  return `${productId}|-${removed}|+${extras}|${customization.notes.toLowerCase()}`;
};

/** Preço do lanche + adicionais × quantidade de cada um. */
export const unitPriceOf = (price: number, customization: Customization | null) =>
  round2(price + (customization?.extras ?? []).reduce((acc, e) => acc + e.price * e.quantity, 0));

/** Adiciona `quantity` unidades; se já existe uma linha idêntica, soma nela. */
export const addToCart = (
  items: CartItem[],
  product: Product,
  customization: Customization | null = null,
  quantity = 1,
): CartItem[] => {
  const normalized = normalizeCustomization(customization);
  const lineId = lineIdFor(product.id, normalized);
  if (items.some((item) => item.lineId === lineId)) {
    return items.map((item) =>
      item.lineId === lineId ? { ...item, quantity: item.quantity + quantity } : item,
    );
  }
  return [
    ...items,
    { ...product, lineId, quantity, unitPrice: unitPriceOf(product.price, normalized), customization: normalized },
  ];
};

export const incrementInCart = (items: CartItem[], lineId: string): CartItem[] =>
  items.map((item) => (item.lineId === lineId ? { ...item, quantity: item.quantity + 1 } : item));

/** Tira 1; se era o último, remove a linha. */
export const decrementInCart = (items: CartItem[], lineId: string): CartItem[] => {
  const itemExists = items.find((item) => item.lineId === lineId);
  if (!itemExists) return items;
  if (itemExists.quantity > 1) {
    return items.map((item) =>
      item.lineId === lineId ? { ...item, quantity: item.quantity - 1 } : item,
    );
  }
  return items.filter((item) => item.lineId !== lineId);
};

/** Botão de lixeira do carrinho: remove a linha inteira. */
export const removeFromCart = (items: CartItem[], lineId: string): CartItem[] =>
  items.filter((item) => item.lineId !== lineId);

export const cartSubtotal = (items: CartItem[]) =>
  round2(items.reduce((acc, item) => acc + item.unitPrice * item.quantity, 0));

export const cartCount = (items: CartItem[]) =>
  items.reduce((acc, item) => acc + item.quantity, 0);

/** Total de unidades de um produto, somando todas as personalizações. */
export const quantityOfProduct = (items: CartItem[], productId: ProductId) =>
  items.reduce((acc, item) => (item.id === productId ? acc + item.quantity : acc), 0);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const parseCustomization = (raw: unknown): Customization | null => {
  if (!isRecord(raw)) return null;
  const removed: Ingredient[] = (Array.isArray(raw.removed) ? raw.removed : [])
    .filter(isRecord)
    .flatMap((r) => (typeof r.id === "number" && typeof r.name === "string" ? [{ id: r.id, name: r.name }] : []));
  const extras: SelectedExtra[] = (Array.isArray(raw.extras) ? raw.extras : [])
    .filter(isRecord)
    .flatMap((e) => {
      const quantity = Number(e.quantity);
      const price = Number(e.price);
      return typeof e.id === "number" && typeof e.name === "string" && quantity > 0 && Number.isFinite(price)
        ? [{ id: e.id, name: e.name, price, quantity }]
        : [];
    });
  return normalizeCustomization({ removed, extras, notes: typeof raw.notes === "string" ? raw.notes : "" });
};

/**
 * Lê o carrinho salvo. Carrinhos da versão anterior guardavam `price` como string
 * e não tinham lineId/personalização — normalizamos aqui.
 */
export const readStoredCart = (): CartItem[] => {
  try {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    const parsed: unknown = saved ? JSON.parse(saved) : [];
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(isRecord).flatMap((raw) => {
      const id = raw.id;
      const quantity = Number(raw.quantity);
      const price = Number(raw.price);
      if ((typeof id !== "number" && typeof id !== "string") || !(quantity > 0) || !Number.isFinite(price)) {
        return [];
      }
      const customization = parseCustomization(raw.customization);
      const item: CartItem = {
        id,
        name: String(raw.name ?? ""),
        description: typeof raw.description === "string" ? raw.description : null,
        price,
        category: String(raw.category ?? ""),
        image_url: typeof raw.image_url === "string" ? raw.image_url : null,
        ...(raw.is_builder === true && { is_builder: true }),
        quantity,
        lineId: lineIdFor(id, customization),
        unitPrice: unitPriceOf(price, customization),
        customization,
      };
      return [item];
    });
  } catch {
    return [];
  }
};
